import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import type { AuthGateState } from '@/hooks/useAuthGate';
import { Icon } from './Icon';
import { Spinner } from './Spinner';

interface AuthGateProps<T> {
  state: AuthGateState<T>;
  /** isRefreshing – идёт повторный запрос (смена страницы/фильтра), данные – ещё старые. */
  children: (data: T, isRefreshing: boolean) => ReactNode;
}

/**
 * Гейт для данных отдельной страницы (не для входа в приложение целиком – тем
 * занимается RootGate).
 *
 * loading – только на самый первый заход; anon (401) – сессия протухла между
 * RootGate и загрузкой данных страницы, уводим на /login; error – всё
 * остальное, показываем причину на месте страницы. Разделение важно: 403 у
 * пользователя с урезанной ролью — это «сюда нельзя», а не «войдите заново»,
 * и выкидывать его на форму входа нельзя (сессия-то живая).
 *
 * Повторные загрузки (смена страницы/фильтра) не сбрасывают на loading – см.
 * useAuthGate.isRefreshing.
 */
export function AuthGate<T>({ state, children }: AuthGateProps<T>) {
  if (state.kind === 'loading') {
    return (
      <section className="gate">
        <Spinner />
      </section>
    );
  }

  if (state.kind === 'anon') {
    return <Navigate to="/login" replace />;
  }

  if (state.kind === 'error') {
    return (
      <section className="gate gate--error">
        <span className="gate__icon">
          <Icon name="lock" size={22} />
        </span>
        <p className="gate__message">{state.message}</p>
      </section>
    );
  }

  return <>{children(state.data, state.isRefreshing)}</>;
}
